import { MenuItem, TextField, Backdrop, Box, Button, Grid, styled, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAddRiwayatPendidikan } from "../../../../api/effects/mutations/RiwayatPendidikan/addRiwayatPendidikan";
import { useEditRiwayatPendidikan } from "../../../../api/effects/mutations/RiwayatPendidikan/editRiwayatPendidikan";
import { useFetchRiwayatPendidikan } from "../../../../api/effects/mutations/RiwayatPendidikan/useFetchRiwayatPendidikan";
import useFetchAllPendidikanSelection from "../../../../api/effects/queries/RiwayatPendidikan/useFetchAllPendidikanSelection";
import InputDate from "../../../../components/InputDate";
import InputRadio from "../../../../components/InputRadio";
import InputSelect from "../../../../components/InputSelect";
import InputTextField from "../../../../components/InputTextField";
import { PendidikanResponse, PendidikanSelectionResponse } from "../../../../models/Pendidikan";
import { formatDate, convertDate, toIsoStringDate } from "../../../../utils/dateConvert";
import InputDateForm from "../../../../components/InputDateForm";
import { InputTextFieldForm } from "../../../../components/InputTextFieldForm";
import InputSelectForm from "../../../../components/InputSelectForm";

const SquareGrid = styled(Grid)(({ theme }) => ({
  backgroundColor: "#FFF",
  height: "auto",
  width: "90%",
  boxShadow: "1px 1px 10px #888888",
  borderRadius: "10px",
  padding: "1%",
  [theme.breakpoints.down("sm")]: {
    padding: "10px",
  },
  margin: "1%",
}));

const FullButton = styled(Button)(({ theme }) => ({
  width: "70%",
}));

const InputWrapperGrid = styled(Grid)(() => ({
  padding: "1%",
}));

const TitleGrid = styled(Grid)(() => ({
  paddingLeft: "10%",
}));

const FullTextField = styled(TextField)(() => ({
  width: "80%",
}));

const ScrollGrid = styled(Grid)(({ theme }) => ({
  overflow: "auto",
  maxHeight: "90vh",
}));

type FormValues = {
  pendidikan: string;
  namaInstitusi: string;
  kota: string;
  jurusan: string;
  tahunMulai: string;
  tahunSelesai: string;
  pembiayaan: string;
  lulus: boolean;
};

const schema = z.object({
  pendidikan: z.string().min(1, "Please choose pendidikan terakhir"),
  namaInstitusi: z.string().min(1, "Nama Institusi field is required"),
  kota: z.string().min(1, "Kota field is required"),
  jurusan: z.string().min(1, "Jurusan field is required"),
  // Tahun Mulai
  tahunMulai: z.string({ required_error: "Tanggal Mulai field is required" })
    .min(1, "Tanggal Mulai is required").refine(val => {
      const date = new Date(val);
      return !isNaN(date.getTime()); // Check if the date is valid
    }, { message: "Invalid date format" })
    .refine(val => {
      const date = new Date(val);
      const minDate = new Date(1900, 0, 1);
      const maxDate = new Date();
      return date >= minDate && date <= maxDate; // Check if the date is within the valid range
    }, { message: "Date must be between January 1, 1900 and today" }),
  // Tahun Selesai
  tahunSelesai: z.string({ required_error: "Tahun Selesai field is required" })
    .min(1, "Tanggal Selesai is required").refine(val => {
      const date = new Date(val);
      return !isNaN(date.getTime()); // Check if the date is valid
    }, { message: "Invalid date format" })
    .refine(val => {
      const date = new Date(val);
      const minDate = new Date(1900, 0, 1);
      const maxDate = new Date();
      maxDate.setFullYear(maxDate.getFullYear() + 10);
      return date >= minDate && date <= maxDate; // Check if the date is within the valid range
    }, { message: "Date must be between January 1, 1900 and 10 years from today" }),
  pembiayaan: z.string().min(1, "Pembiayaan is required"),
  lulus: z.boolean(),
})
  .refine((data) => {
    if (data.tahunMulai && data.tahunSelesai) {
      return new Date(data.tahunSelesai) >= new Date(data.tahunMulai);
    }
    return true;
  }, {
    message: "End Date Can't be before Start Date",
    path: ["tahunSelesai"],
  }
  );

type Props = {
  open: boolean;
  handleClose: () => void;
  ID?: number;
};

export default function RiwayatPendidikanModal({
  ID = -1,
  open,
  handleClose,
}: Props) {
  const [selectionData, setSelectionData] = useState<PendidikanSelectionResponse[]>([]);

  const { data: PendidikanData } = useFetchAllPendidikanSelection();
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<FormValues>({
    mode: "onSubmit",
    resolver: zodResolver(schema),
    defaultValues: {
      pendidikan: "",
      namaInstitusi: "",
      kota: "",
      jurusan: "",
      pembiayaan: "",
      lulus: false,
      tahunMulai: "",
      tahunSelesai: ""
    },
  });

  const { mutateAsync: GetRiwayatPendidikanData } = useFetchRiwayatPendidikan({
    onSuccess(data) {
      setValue("pendidikan", String(data.PENDIDIKAN_ID ?? ""));
      setValue("namaInstitusi", data.NAMA_INSTITUSI);
      setValue("kota", data.KOTA);
      setValue("jurusan", data.JURUSAN);
      setValue("tahunMulai", formatDate(data.TAHUN_MULAI));
      setValue("tahunSelesai", formatDate(data.TAHUN_SELESAI));
      setValue("pembiayaan", data.PEMBIAYAAN);
      setValue("lulus", data.LULUS);
      console.log("Success");
    },
    onError() {
      console.log("Error");
    },
  });

  useEffect(() => {
    if (ID > 0) {
      GetRiwayatPendidikanData(ID);
    }
  }, [open, ID, GetRiwayatPendidikanData]);

  useEffect(() => {
    if (PendidikanData !== undefined) {
      setSelectionData(PendidikanData);
    }
    if (!open) {
      reset();
    }
  }, [PendidikanData, open, reset]);

  const { mutateAsync: AddRiwayatPendidikan, isLoading: isAdding } = useAddRiwayatPendidikan({
    onSuccess() {
      handleModalClose();
    },
    onError() {
      console.log("Error");
    },
  });

  const { mutateAsync: EditRiwayatPendidikan, isLoading: isEditing } = useEditRiwayatPendidikan({
    onSuccess() {
      handleModalClose();
    },
    onError() {
      console.log("Error");
    },
  });

  const handleModalClose = () => {
    reset();
    handleClose();
  };

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    const payload = {
      PENDIDIKAN_ID: data.pendidikan,
      NAMA_INSTITUSI: data.namaInstitusi,
      KOTA: data.kota,
      JURUSAN: data.jurusan,
      TAHUN_MULAI: toIsoStringDate(data.tahunMulai),
      TAHUN_SELESAI: toIsoStringDate(data.tahunSelesai),
      PEMBIAYAAN: data.pembiayaan,
      LULUS: data.lulus,
    };
    try {
      if (ID > 0) {
        await EditRiwayatPendidikan({
          RIWAYAT_PENDIDIKAN_ID: ID,
          ...payload,
        });
      } else {
        await AddRiwayatPendidikan(payload);
      }
    } catch (error) {
      console.error("Submission failed:", error);
    }
  };

  return (
    <Backdrop
      sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      open={open}
    >
      <Box component={"form"} onSubmit={handleSubmit(onSubmit)} noValidate>
        <SquareGrid container>
          <ScrollGrid container>
            <Grid container justifyContent="flex-start" paddingLeft={"10.5%"} item xs={6}
              paddingBottom={
                {
                  xs: 5,
                  sm: 5,
                  md: 5,
                  lg: 5,
                  xl: 5
                }}>
              <Typography variant="subtitle1" color="black">
                {ID === -1 ? "Tambah Riwayat Pendidikan" : "Ubah Riwayat Pendidikan"}
              </Typography>
            </Grid>
            <Grid container justifyContent="flex-end" item xs={6}>
              <Button onClick={handleModalClose}>Close</Button>
            </Grid>

            {/* Institusi - Text Field */}
            <InputTextFieldForm
              name="namaInstitusi"
              control={control}
              label="Institusi"
              error={errors.namaInstitusi}
            />
            {/* Kota - Text Field */}
            <InputTextFieldForm
              name="kota"
              control={control}
              label="Kota"
              error={errors.kota}
            />

            {/* Pendidikan - Select */}
            <InputSelectForm
              name="pendidikan"
              title="Pendidikan"
              control={control}
              options={selectionData}
              placeholder="-- Pilih Pendidikan --"
            />

            {/* Jurusan - Text Field */}
            <InputTextFieldForm
              name="jurusan"
              control={control}
              label="Jurusan"
              error={errors.jurusan}
            />

            {/* Tahun Mulai - Date */}
            <InputDateForm
              name="tahunMulai"
              control={control}
              label="Tanggal Mulai"
              error={errors.tahunMulai}
              disabled={false}
            />

            {/* Tahun Selesai - Date */}
            <InputDateForm
              name="tahunSelesai"
              control={control}
              label="Tanggal Selesai"
              error={errors.tahunSelesai}
              disabled={false}
            />

            {/* Pembiayaan - Text Field */}
            <InputTextFieldForm
              name="pembiayaan"
              control={control}
              label="Pembiayaan"
              error={errors.pembiayaan}
            />
            {/* Lulus - Radio */}
            <Grid container justifyContent="center" item xs={12}>
              <Controller
                name="lulus"
                control={control}
                render={({ field }) => (
                  <InputRadio
                    title={"Lulus"}
                    value={field.value}
                    handleEdit={field.onChange}
                  />
                )}
              />
            </Grid>

            <Grid container justifyContent="center" item xs={12}
              paddingTop={{
                xs: 2,
                sm: 2,
                md: 2,
                lg: 2,
                xl: 2
              }}
              paddingBottom={{
                xs: 2,
                sm: 2,
                md: 2,
                lg: 2,
                xl: 2
              }}>
              <FullButton variant="contained" type="submit" disabled={isAdding || isEditing}>
                {(isAdding || isEditing) ? "Saving..." : "Save"}
              </FullButton>
            </Grid>
          </ScrollGrid>
        </SquareGrid>
      </Box>
    </Backdrop >
  );
}
